import { BookOpenText, CornerDownLeft, FolderKanban, Inbox, Keyboard, ListTodo, Moon, Plus, Search, Settings, Shield, Sun, UserRound } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearch } from '../api/integrations';
import { useProjects } from '../api/projects';
import { Kbd } from '../components/ui/Badge';
import { useEscapeLayer } from '../components/ui/escape';
import { LayerProvider } from '../components/ui/layer';
import { cn } from '../lib/cn';
import { matchesSearch } from '../lib/format';
import { useDebounced } from '../lib/hooks';
import { useVisibleModules } from './modules';
import { useSession } from './session';
import { useShell } from './shell-context';
import { useTheme } from './theme';

type Item = { id: string; group: string; label: string; hint?: string; icon: ReactNode; shortcut?: string; run: () => void };

/**
 * Панель команд (Ctrl/Cmd+K): дії, переходи, проєкти, задачі й люди з пошуку сервера.
 * Відкривається й закривається без анімації — нею користуються десятки разів на день.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [container, setContainer] = useState<HTMLDialogElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const debounced = useDebounced(q, 150);
  const search = useSearch(debounced);
  const projects = useProjects();
  const navigate = useNavigate();
  const shell = useShell();
  const theme = useTheme();
  const { isAdmin, can } = useSession();
  const modules = useVisibleModules(can);

  useEscapeLayer(open, onClose);
  const setDialogRef = useCallback((el: HTMLDialogElement | null) => {
    ref.current = el;
    setContainer(el);
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      setQ('');
      setActive(0);
      inputRef.current?.focus();
    } else if (!open && el.open) el.close();
  }, [open]);

  const go = useCallback(
    (fn: () => void) => {
      onClose();
      window.setTimeout(fn, 0);
    },
    [onClose],
  );

  const items = useMemo<Item[]>(() => {
    const term = q.trim();
    const base: Item[] = [
      { id: 'new', group: 'Дії', label: 'Нова задача', icon: <Plus className="size-4" />, shortcut: 'Q', run: () => go(() => shell.openCreateTask()) },
      { id: 'theme', group: 'Дії', label: theme.resolved === 'dark' ? 'Світла тема' : 'Темна тема', icon: theme.resolved === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />, run: () => go(() => theme.setPref(theme.resolved === 'dark' ? 'light' : 'dark')) },
      { id: 'keys', group: 'Дії', label: 'Гарячі клавіші', icon: <Keyboard className="size-4" />, shortcut: '?', run: () => go(() => shell.openShortcuts()) },
      { id: 'my', group: 'Перейти', label: 'Моя робота', icon: <ListTodo className="size-4" />, run: () => go(() => navigate('/my-work')) },
      { id: 'inbox', group: 'Перейти', label: 'Вхідні', icon: <Inbox className="size-4" />, run: () => go(() => navigate('/inbox')) },
      { id: 'projects', group: 'Перейти', label: 'Проєкти', icon: <FolderKanban className="size-4" />, run: () => go(() => navigate('/projects')) },
      { id: 'search', group: 'Перейти', label: term ? `Шукати «${term}»` : 'Пошук', icon: <Search className="size-4" />, run: () => go(() => navigate(`/search${term ? `?q=${encodeURIComponent(term)}` : ''}`)) },
      { id: 'knowledge', group: 'Перейти', label: 'Довідник понять', icon: <BookOpenText className="size-4" />, run: () => go(() => navigate('/knowledge/terms')) },
      { id: 'settings', group: 'Перейти', label: 'Налаштування', icon: <Settings className="size-4" />, run: () => go(() => navigate('/settings')) },
      ...(isAdmin ? [{ id: 'admin', group: 'Перейти', label: 'Адміністрування', icon: <Shield className="size-4" />, run: () => go(() => navigate('/admin')) }] : []),
      ...modules.flatMap((m) =>
        (m.nav ?? [{ to: m.basePath, label: m.label, icon: m.icon }]).map((n) => {
          const Icon = n.icon ?? m.icon;
          return { id: `m-${n.to}`, group: 'Перейти', label: n.label, icon: <Icon className="size-4" />, run: () => go(() => navigate(n.to)) };
        }),
      ),
    ];
    const filtered = term ? base.filter((i) => i.id === 'search' || matchesSearch(i.label, term)) : base;
    const proj: Item[] = (projects.data ?? [])
      .filter((p) => !p.archivedAt && (!term || matchesSearch(`${p.name} ${p.processCode ?? ''}`, term)))
      .slice(0, term ? 6 : 4)
      .map((p) => ({ id: `p-${p.id}`, group: 'Проєкти', label: p.name, hint: p.processCode ? `Процес ${p.processCode}` : undefined, icon: <FolderKanban className="size-4" />, run: () => go(() => navigate(`/projects/${p.id}`)) }));
    const tasks: Item[] =
      term.length >= 2
        ? (search.data?.tasks ?? []).slice(0, 8).map((t) => ({
            id: `t-${t.id}`,
            group: 'Задачі',
            label: t.title,
            hint: `${t.key}${t.status === 'completed' ? ' · завершена' : ''}`,
            icon: <span className="key text-caption text-fg-3">Т</span>,
            run: () => go(() => shell.openTask(t.id)),
          }))
        : [];
    const people: Item[] =
      term.length >= 2
        ? (search.data?.users ?? []).slice(0, 4).map((u) => ({
            id: `u-${u.id}`,
            group: 'Працівники',
            label: u.name,
            hint: u.email,
            icon: <UserRound className="size-4" />,
            run: () => go(() => navigate(`/search?q=${encodeURIComponent(u.name)}`)),
          }))
        : [];
    return [...(term ? [...tasks, ...proj, ...filtered, ...people] : [...filtered, ...proj])];
  }, [q, search.data, projects.data, isAdmin, modules, go, navigate, shell, theme]);

  useEffect(() => {
    if (active >= items.length) setActive(0);
  }, [items.length, active]);

  let lastGroup = '';
  return (
    <dialog
      ref={setDialogRef}
      data-ers-layer=""
      aria-label="Панель команд"
      onCancel={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className="m-0 mx-auto mt-[12dvh] w-[calc(100%-24px)] max-w-[620px] overflow-hidden rounded-lg border border-line bg-raised p-0 text-fg shadow-pop"
    >
      <LayerProvider container={container}>
        {open && (
          <div className="flex max-h-[min(70dvh,560px)] flex-col">
            <div className="flex items-center gap-2.5 border-b border-line px-3.5">
              <Search className="size-4 shrink-0 text-fg-3" aria-hidden />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActive((a) => Math.min(items.length - 1, a + 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActive((a) => Math.max(0, a - 1));
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    items[active]?.run();
                  }
                }}
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
                aria-label="Команда або пошук"
                placeholder="Команда, проєкт або задача (Т-12)…"
                className="h-12 w-full bg-transparent text-lead outline-none placeholder:text-fg-3"
              />
              <Kbd>Esc</Kbd>
            </div>
            {/* flex-auto: блок має висоту за вмістом — з основою 0% (flex-1) Safari стискав би список до нуля. */}
            <ul id={listId} role="listbox" aria-label="Результати" className="min-h-0 flex-auto overflow-y-auto overscroll-contain p-1.5">
              {items.length === 0 && <li className="px-3 py-6 text-center text-meta text-fg-3">{search.isFetching ? 'Пошук…' : 'Нічого не знайдено'}</li>}
              {items.map((it, i) => {
                const head = it.group !== lastGroup;
                lastGroup = it.group;
                return (
                  <li key={it.id} role="presentation">
                    {head && <div className="px-2.5 pt-2.5 pb-1 text-caption font-medium text-fg-3">{it.group}</div>}
                    <div
                      id={`${listId}-${i}`}
                      role="option"
                      aria-selected={i === active}
                      onPointerMove={() => setActive(i)}
                      onClick={() => it.run()}
                      className={cn('flex h-9 cursor-pointer items-center gap-3 rounded-sm px-2.5', i === active && 'bg-surface-2')}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center text-fg-3">{it.icon}</span>
                      <span className="min-w-0 flex-1 truncate text-body">{it.label}</span>
                      {it.hint && <span className="key shrink-0 text-caption text-fg-3">{it.hint}</span>}
                      {it.shortcut && <Kbd>{it.shortcut}</Kbd>}
                      {i === active && <CornerDownLeft className="size-3.5 shrink-0 text-fg-3" aria-hidden />}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center gap-3 border-t border-line px-3.5 py-2 text-caption text-fg-3">
              <span>
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> вибір
              </span>
              <span>
                <Kbd>Enter</Kbd> виконати
              </span>
            </div>
          </div>
        )}
      </LayerProvider>
    </dialog>
  );
}
